import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  writeBatch,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db, default as firebaseApp } from '../firebase';
import { GoogleUser, TradingAccount } from '../types';
import { logger } from '../utils/logger';

/** Firestore batch 上限 500,預留餘裕 */
const SAFE_BATCH_SIZE = 450;

export class MultiAccountService {
  private static readonly GOOGLE_USERS_COLLECTION = 'googleUsers';
  private static readonly TRADING_ACCOUNTS_COLLECTION = 'tradingAccounts';

  /**
   * SCHEMA-01:帳號成員同步(fan-out)
   * 依據所有 googleUsers.allowedAccounts 計算帳號成員清單,
   * 同步寫入 tradingAccounts / portfolios / watchlists / 全部 transactions
   * 的 allowedUsers 欄位(rules 限權的唯一依據)。
   * 冪等:以「目標狀態」覆寫,可安全重跑。
   */
  static async syncAccountMembership(accountId: string): Promise<void> {
    const googleUsers = await this.getAllGoogleUsers();
    const members = googleUsers
      .filter((u) => u.allowedAccounts?.includes(accountId))
      .map((u) => u.uid);

    // 1) tradingAccounts / portfolios / watchlists(單一文件)
    const singleDocUpdates: Promise<void>[] = [
      updateDoc(doc(db, this.TRADING_ACCOUNTS_COLLECTION, accountId), {
        allowedUsers: members,
        updatedAt: serverTimestamp(),
      }).catch((error) => {
        logger.multiAccount.error('Failed to sync allowedUsers on tradingAccount', { accountId, error });
        throw error;
      }),
    ];

    for (const coll of ['portfolios', 'watchlists']) {
      const ref = doc(db, coll, accountId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        singleDocUpdates.push(
          updateDoc(ref, { allowedUsers: members, updatedAt: serverTimestamp() })
        );
      }
    }

    // 2) transactions(批次,每批 ≤ SAFE_BATCH_SIZE)
    // admin 可用 accountId 查詢(isAdmin 分支不受 array-contains 限制)
    const txQuery = query(collection(db, 'transactions'), where('accountId', '==', accountId));
    const txSnapshot = await getDocs(txQuery);
    const txUpdates: Promise<void>[] = [];
    const docRefs = txSnapshot.docs.map((d) => d.ref);
    for (let i = 0; i < docRefs.length; i += SAFE_BATCH_SIZE) {
      const chunk = docRefs.slice(i, i + SAFE_BATCH_SIZE);
      const batch = writeBatch(db);
      chunk.forEach((ref) => batch.update(ref, { allowedUsers: members }));
      txUpdates.push(batch.commit());
    }

    await Promise.all([...singleDocUpdates, ...txUpdates]);
    logger.multiAccount.info('Account membership synced', { accountId, members: members.length, transactions: docRefs.length });
  }

  // Google User Management
  static async createGoogleUser(googleUser: GoogleUser): Promise<GoogleUser> {
    try {
      // FIN-14:uid 為必要欄位,缺失即拒絕(避免寫入 googleUsers/undefined)
      if (!googleUser.uid) {
        throw new Error('googleUser.uid is required');
      }

      const userRef = doc(db, this.GOOGLE_USERS_COLLECTION, googleUser.uid);

      // Check if user already exists
      const existingUser = await getDoc(userRef);
      if (existingUser.exists()) {
        throw new Error('Google user already exists');
      }

      // Filter out undefined values before sending to Firestore
      const filteredEntries = Object.entries(googleUser).filter(([_, value]) => value !== undefined);
      const userData = Object.fromEntries(filteredEntries);

      await setDoc(userRef, {
        ...userData,
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
      });

      return googleUser;
    } catch (error) {
      logger.multiAccount.error('Failed to create Google user', error);
      throw new Error('Failed to create Google user');
    }
  }

  static async createOrUpdateGoogleUser(googleUser: Partial<GoogleUser>): Promise<GoogleUser> {
    try {
      // FIN-14:uid 為必要欄位
      if (!googleUser.uid) {
        throw new Error('googleUser.uid is required');
      }
      const userRef = doc(db, this.GOOGLE_USERS_COLLECTION, googleUser.uid);
      const existingUser = await getDoc(userRef);

      // FIN-14(完整):依「首次建立/既有更新」分流 —
      // 既有更新只寫登入相關欄位並用 merge,絕不覆蓋管理員設定的
      // role/isAllowed/allowedAccounts(舊版全量 setDoc 在 admin 變更權限
      // 與本次寫入之間的視窗會把舊值蓋回去)
      if (existingUser.exists()) {
        const existing = existingUser.data();
        await setDoc(userRef, {
          displayName: googleUser.displayName ?? existing.displayName,
          photoURL: googleUser.photoURL ?? null,
          email: googleUser.email ?? existing.email,
          lastLoginAt: serverTimestamp(),
        }, { merge: true });

        return {
          ...existing,
          displayName: googleUser.displayName ?? existing.displayName,
          photoURL: googleUser.photoURL ?? existing.photoURL,
          email: googleUser.email ?? existing.email,
          lastLoginAt: new Date(),
        } as GoogleUser;
      }

      // 首次建立:含 pending 預設值(rules create 要求 role/user、
      // isAllowed/false、allowedAccounts/[] 三項釘住)
      const userData: GoogleUser = {
        uid: googleUser.uid,
        email: googleUser.email ?? '',
        displayName: googleUser.displayName ?? 'User',
        photoURL: googleUser.photoURL,
        isAllowed: false,
        allowedAccounts: [],
        defaultAccountId: undefined,
        role: 'user',
        createdAt: new Date(),
        lastLoginAt: new Date(),
      };

      await setDoc(userRef, {
        ...userData,
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
      });

      return userData;
    } catch (error) {
      logger.multiAccount.error('Failed to create or update Google user', error);
      throw new Error('Failed to create or update Google user');
    }
  }

  /**
   * 訂閱自身 googleUsers 文件(ARCH-08)
   * 管理員核准/調整權限後,使用者端狀態即時更新,不需重新登入
   */
  static subscribeToGoogleUser(
    uid: string,
    callback: (googleUser: GoogleUser | null) => void
  ): () => void {
    const userRef = doc(db, this.GOOGLE_USERS_COLLECTION, uid);

    return onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        callback({
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLoginAt: data.lastLoginAt?.toDate() || new Date(),
        } as GoogleUser);
      } else {
        callback(null);
      }
    }, (error) => {
      logger.multiAccount.error('googleUsers subscription error', { uid, error });
    });
  }

  static async getGoogleUser(uid: string): Promise<GoogleUser | null> {
    try {
      const userRef = doc(db, this.GOOGLE_USERS_COLLECTION, uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const data = userDoc.data();
        return {
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLoginAt: data.lastLoginAt?.toDate() || new Date(),
        } as GoogleUser;
      }

      return null;
    } catch (error) {
      logger.multiAccount.error('Failed to get Google user', error);
      return null;
    }
  }

  /**
   * 列出所有 googleUsers(僅 admin 可用 — rules 以 isAdmin() 限權 list)
   * 失敗時拋錯,讓管理 UI 顯示錯誤而非誤導性的「目前沒有用戶」
   */
  static async getAllGoogleUsers(): Promise<GoogleUser[]> {
    const querySnapshot = await getDocs(collection(db, this.GOOGLE_USERS_COLLECTION));
    const users: GoogleUser[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      users.push({
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        lastLoginAt: data.lastLoginAt?.toDate() || new Date(),
      } as GoogleUser);
    });

    return users;
  }

  // Subscribe to account balance changes
  static subscribeToAccountBalance(
    accountId: string,
    callback: (account: TradingAccount | null) => void
  ): () => void {
    try {
      const accountRef = doc(db, this.TRADING_ACCOUNTS_COLLECTION, accountId);

      return onSnapshot(accountRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          const account: TradingAccount = {
            id: doc.id,
            ...data,
            // 向下相容：如果沒有 initialBalance，使用 balance 作為預設值
            initialBalance: data.initialBalance ?? data.balance,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as TradingAccount;

          callback(account);
        } else {
          callback(null);
        }
      }, (error) => {
        // 監聽錯誤保留最後已知狀態
        logger.multiAccount.error('Error in account subscription', { accountId, error });
      });
    } catch (error) {
      logger.multiAccount.error('Failed to subscribe to account balance', { accountId, error });
      return () => {};
    }
  }

  /**
   * 更新使用者權限(admin 操作 — 由 rules isAdmin() 強制)
   * 權限變更後自動 fan-out 同步帳號文件的 allowedUsers(SCHEMA-01)
   */
  static async updateGoogleUserPermissions(
    uid: string,
    isAllowed: boolean,
    allowedAccounts: string[],
    role: 'admin' | 'user' | 'viewer' = 'user'
  ): Promise<void> {
    try {
      const userRef = doc(db, this.GOOGLE_USERS_COLLECTION, uid);

      // 必須在寫入「之前」讀取舊值 — 若在 updateDoc 之後才讀,
      // beforeAccounts 會等於新的 allowedAccounts,被移除的帳號永遠不會
      // 觸發 syncAccountMembership,撤銷形同無效(安全漏洞)
      const beforeSnap = await getDoc(userRef);
      const beforeAccounts: string[] = beforeSnap.exists()
        ? (beforeSnap.data().allowedAccounts ?? [])
        : [];

      await updateDoc(userRef, {
        isAllowed,
        allowedAccounts,
        role,
        updatedAt: serverTimestamp(),
      });

      // 若使用者的預設帳號在被移除的清單中,一併清除 —
      // 否則 AppContext 的 find() 找不到目標帳號,使用者會卡在「無選定帳戶」狀態
      const removedAccounts = beforeAccounts.filter(
        (accountId) => !allowedAccounts.includes(accountId)
      );
      const currentDefault = beforeSnap.data()?.defaultAccountId;
      if (currentDefault && removedAccounts.includes(currentDefault)) {
        await updateDoc(userRef, {
          defaultAccountId: null,
          updatedAt: serverTimestamp(),
        });
      }

      // fan-out:同步「新舊清單聯集」的所有帳號
      // (撤銷的帳號只出現在舊清單、新授予的只出現在新清單,兩邊都要同步)
      const changedAccounts = Array.from(new Set([...allowedAccounts, ...beforeAccounts]));

      const syncWithRetry = async (accountId: string) => {
        try {
          await this.syncAccountMembership(accountId);
        } catch (firstError) {
          // 撤銷未同步 = 撤銷無效,屬安全相關路徑 → 重試一次
          logger.multiAccount.warn('Membership fan-out failed, retrying', { accountId, uid, error: firstError });
          try {
            await this.syncAccountMembership(accountId);
          } catch (error) {
            // 仍失敗:冪等可重跑(管理員重新儲存權限即會重觸發),記錄明確錯誤
            logger.multiAccount.error('Membership fan-out failed after retry — 撤銷可能未同步,請重新儲存權限', { accountId, uid, error });
          }
        }
      };

      await Promise.all(changedAccounts.map(syncWithRetry));
    } catch (error) {
      logger.multiAccount.error('Failed to update Google user permissions', error);
      throw new Error('Failed to update user permissions');
    }
  }

  static async setDefaultAccount(uid: string, accountId: string): Promise<void> {
    try {
      const userRef = doc(db, this.GOOGLE_USERS_COLLECTION, uid);
      await updateDoc(userRef, {
        defaultAccountId: accountId,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      logger.multiAccount.error('Failed to set default account', error);
      throw new Error('Failed to set default account');
    }
  }

  // Trading Account Management
  static async createTradingAccount(accountData: Omit<TradingAccount, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const accountRef = doc(collection(db, this.TRADING_ACCOUNTS_COLLECTION));

      // SCHEMA-01:建立者即 owner,allowedUsers 至少包含建立者(rules create 驗證)
      const creatorUid = getAuth(firebaseApp).currentUser?.uid;
      const ownerUid = accountData.ownerUid ?? creatorUid;
      const allowedUsers = Array.from(new Set([
        ...(accountData.allowedUsers ?? []),
        ...(ownerUid ? [ownerUid] : []),
      ]));

      // 確保新帳戶有 initialBalance 欄位
      const dataToSave = {
        ...accountData,
        ownerUid,
        allowedUsers,
        // 如果沒有提供 initialBalance，使用 balance 作為預設值
        initialBalance: accountData.initialBalance ?? accountData.balance,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(accountRef, dataToSave);
      return accountRef.id;
    } catch (error) {
      logger.multiAccount.error('Failed to create trading account', error);
      throw new Error('Failed to create trading account');
    }
  }

  static async getTradingAccount(accountId: string): Promise<TradingAccount | null> {
    try {
      const accountRef = doc(db, this.TRADING_ACCOUNTS_COLLECTION, accountId);
      const accountDoc = await getDoc(accountRef);

      if (accountDoc.exists()) {
        const data = accountDoc.data();
        return {
          id: accountDoc.id,
          ...data,
          // 向下相容：如果沒有 initialBalance，使用 balance 作為預設值
          initialBalance: data.initialBalance ?? data.balance,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as TradingAccount;
      }

      return null;
    } catch (error) {
      logger.multiAccount.error('Failed to get trading account', error);
      return null;
    }
  }

  /** 列出所有交易帳戶(僅 admin — rules isAdmin() 限權 list) */
  static async getAllTradingAccounts(): Promise<TradingAccount[]> {
    const querySnapshot = await getDocs(collection(db, this.TRADING_ACCOUNTS_COLLECTION));
    const accounts: TradingAccount[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      accounts.push({
        id: doc.id,
        ...data,
        initialBalance: data.initialBalance ?? data.balance,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as TradingAccount);
    });

    return accounts;
  }

  static async getUserTradingAccounts(googleUid: string): Promise<TradingAccount[]> {
    const googleUser = await this.getGoogleUser(googleUid);
    if (!googleUser || !googleUser.allowedAccounts.length) {
      return [];
    }

    // 平行讀取各帳戶(取代逐一序列 await 的 N+1)
    const accounts = await Promise.all(
      googleUser.allowedAccounts.map((accountId) => this.getTradingAccount(accountId))
    );

    return accounts.filter((account): account is TradingAccount => account !== null);
  }

  static async updateTradingAccount(accountId: string, updates: Partial<TradingAccount>): Promise<void> {
    try {
      const accountRef = doc(db, this.TRADING_ACCOUNTS_COLLECTION, accountId);
      // allowedUsers 為 rules 管理欄位,僅經由 syncAccountMembership 變更
      const { allowedUsers: _excluded, ...safeUpdates } = updates as Partial<TradingAccount> & { allowedUsers?: string[] };
      await updateDoc(accountRef, {
        ...safeUpdates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      logger.multiAccount.error('Failed to update trading account', error);
      throw new Error('Failed to update trading account');
    }
  }

  /**
   * 刪除交易帳戶與所有關聯資料(FIN-13:分批刪除,不受 500 筆 batch 上限限制)
   */
  static async deleteTradingAccount(accountId: string): Promise<void> {
    try {
      // 1. 刪除所有 transactions(分批,每批 ≤ SAFE_BATCH_SIZE)
      const transactionsQuery = query(
        collection(db, 'transactions'),
        where('accountId', '==', accountId)
      );
      const transactionsSnapshot = await getDocs(transactionsQuery);
      const txRefs = transactionsSnapshot.docs.map((d) => d.ref);

      for (let i = 0; i < txRefs.length; i += SAFE_BATCH_SIZE) {
        const batch = writeBatch(db);
        txRefs.slice(i, i + SAFE_BATCH_SIZE).forEach((ref) => batch.delete(ref));
        await batch.commit();
      }
      logger.multiAccount.info('Deleted transactions for account', { accountId, count: txRefs.length });

      // 2. 刪除 portfolio / watchlist / tradingAccount(同一批)
      const finalBatch = writeBatch(db);
      const portfolioRef = doc(db, 'portfolios', accountId);
      if ((await getDoc(portfolioRef)).exists()) finalBatch.delete(portfolioRef);
      const watchlistRef = doc(db, 'watchlists', accountId);
      if ((await getDoc(watchlistRef)).exists()) finalBatch.delete(watchlistRef);
      finalBatch.delete(doc(db, this.TRADING_ACCOUNTS_COLLECTION, accountId));
      await finalBatch.commit();

      // 3. 清理所有 googleUsers 的授權(冪等,失敗可重跑)
      const googleUsers = await this.getAllGoogleUsers();
      await Promise.all(
        googleUsers
          .filter((user) => user.allowedAccounts.includes(accountId))
          .map((user) => {
            const updatedAccounts = user.allowedAccounts.filter((id) => id !== accountId);
            return this.updateGoogleUserPermissions(
              user.uid,
              user.isAllowed,
              updatedAccounts,
              user.role
            ).catch((error) => {
              logger.multiAccount.error('Cleanup of user permission failed (rerunnable)', {
                accountId, uid: user.uid, error,
              });
            });
          })
      );
    } catch (error) {
      logger.multiAccount.error('Failed to delete trading account', { accountId, error });
      throw new Error(`Failed to delete trading account: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Permission Management
  static async grantAccountAccess(googleUid: string, accountId: string): Promise<void> {
    try {
      const googleUser = await this.getGoogleUser(googleUid);
      if (!googleUser) {
        throw new Error('Google user not found');
      }

      const updatedAccounts = Array.from(new Set([...googleUser.allowedAccounts, accountId]));
      await this.updateGoogleUserPermissions(
        googleUid,
        googleUser.isAllowed,
        updatedAccounts,
        googleUser.role
      );
    } catch (error) {
      logger.multiAccount.error('Failed to grant account access', error);
      throw new Error('Failed to grant account access');
    }
  }

  static async revokeAccountAccess(googleUid: string, accountId: string): Promise<void> {
    try {
      const googleUser = await this.getGoogleUser(googleUid);
      if (!googleUser) {
        throw new Error('Google user not found');
      }

      const updatedAccounts = googleUser.allowedAccounts.filter(id => id !== accountId);

      await this.updateGoogleUserPermissions(
        googleUid,
        googleUser.isAllowed,
        updatedAccounts,
        googleUser.role
      );
    } catch (error) {
      logger.multiAccount.error('Failed to revoke account access', error);
      throw new Error('Failed to revoke account access');
    }
  }

  // Utility methods
  static async checkUserAccess(googleUid: string): Promise<{ isAllowed: boolean; accounts: TradingAccount[] }> {
    try {
      const googleUser = await this.getGoogleUser(googleUid);
      if (!googleUser || !googleUser.isAllowed) {
        return { isAllowed: false, accounts: [] };
      }

      const accounts = await this.getUserTradingAccounts(googleUid);
      return { isAllowed: true, accounts };
    } catch (error) {
      // fail-closed:檢查失敗視為無權限
      logger.multiAccount.error('Failed to check user access', error);
      return { isAllowed: false, accounts: [] };
    }
  }
}
